import React, { useState, useEffect, useMemo, useRef } from 'react';
import Loader from "../Shared/Loader/Loader";
import APIConstant from "../../utils/pathConstants";
import api from "../../utils/apiClient";
import CommonConfig from '../../utils/constant';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import Toast from '../Shared/Toast/Toast';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { FileUpload } from 'primereact/fileupload';
import { Tag } from 'primereact/tag';
import { Tooltip } from 'primereact/tooltip';
import { useCatchResponse } from '../../utils/useCatchResponse';
import userImage from '../../assets/images/UserImage.png';
import { apiBase } from '../../utils/config';
import moment from 'moment';
import './StaffAttendance.css';

const StaffAttendance = () => {
    const { catchResponse } = useCatchResponse();
    const [securityUserID, setSecurityUserID] = useState("");
    const [schoolId, setSchoolId] = useState("");
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [teacherList, setTeacherList] = useState([]);
    const [rows, setRows] = useState(10);
    const [first, setFirst] = useState(0);
    const [markAllPresent, setMarkAllPresent] = useState(false);
    const [originalTeacherList, setOriginalTeacherList] = useState([]);
    const [pageAccess, setPageAccess] = useState({});
    const [financialYear, setFinancialYear] = useState('');
    const [academicStartDate, setAcademicStartDate] = useState(null);
    const [academicEndDate, setAcademicEndDate] = useState(null);
    const [isUploadVisible, setIsUploadVisible] = useState(false);
    const fileUploadRef = useRef(null);

    const attendanceOptions = [
        { label: "P", value: "P" },
        { label: "A", value: "A" },
        { label: "L", value: "L" },
    ];

    useEffect(() => {
        const loginData = CommonConfig.loginData();
        setSecurityUserID(loginData?._id);
        const school = CommonConfig.isOrgOwner(loginData?.UserRole) ? loginData?._id : loginData?.CreatedBy;
        setSchoolId(school);
        const accessData = CommonConfig.getUserAccess('Staff Attendance');
        setPageAccess(accessData);
        getFinancialYearData(loginData, school);
    }, []);

    const getFinancialYearData = async (loginData, school) => {
        const years = await CommonConfig.getFinancialYears(school);
        const currentYear = CommonConfig.getCurrentFinancialYear(years);
        const selectedAcYear = CommonConfig.getCookie('selectedAcademicYear');
        const fy = selectedAcYear ?? currentYear?.value;
        setFinancialYear(fy);
        await GetConfigurationData(school, years, fy);
        await GetTeacherList(school, fy, selectedDate);
    };

    const GetConfigurationData = async (id, academicYearList, selectedYear) => {
        try {
            let data = { id: id };
            await api.post(APIConstant.path.GetGeneralConfigurationDetail, data).then(async response => {
                let res = response;
                if (res.success) {
                    if (res?.data?.IsAcademicYearApply) {
                        const academicCalendar = academicYearList || [];
                        if (!academicCalendar.length) {
                            setAcademicStartDate(null);
                            setAcademicEndDate(null);
                            return;
                        }
                        const validRanges = academicCalendar.filter(
                            item => item?.StartDate && item.EndDate && item?._id === selectedYear
                        );
                        if (!validRanges.length) {
                            setAcademicStartDate(null);
                            setAcademicEndDate(null);
                            return;
                        }
                        const sortedCalendar = [...validRanges].sort(
                            (a, b) => new Date(a?.StartDate) - new Date(b?.StartDate)
                        );
                        setAcademicStartDate(new Date(sortedCalendar[0]?.StartDate));
                        setAcademicEndDate(new Date(sortedCalendar[0]?.EndDate));
                    }
                    Loader.hide();
                }
            }).catch(err => {
                Loader.hide();
                console.log(err);
                catchResponse(err);
            });
        } catch (err) {
            console.log(err);
            catchResponse(err);
        }
    };

    const formatDateToYMD = (dateObj) => {
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}/${month}/${day}`;
    };

    const GetTeacherList = async (school, fy, date) => {
        try {
            let formattedDate = formatDateToYMD(date);
            let data = {
                SchoolId: school,
                SelectedDate: formattedDate,
                FinancialYearId: fy,
            };
            Loader.show();
            await api.post(APIConstant.path.GetStaffAttendanceList, data).then(async response => {
                let res = response;
                if (res.success) {
                    setTeacherList(res.data);
                    const allPresent = res.data.length > 0 && res.data.every(t => t?.AttendanceType === 'P');
                    setMarkAllPresent(allPresent);
                    Loader.hide();
                }
            }).catch(err => {
                Loader.hide();
                console.log(err);
                catchResponse(err);
            });
        } catch (err) {
            console.log(err);
            catchResponse(err);
        }
    };

    const summaryData = useMemo(() => {
        const total = teacherList.length;
        const present = teacherList.filter(t => t.AttendanceType === 'P').length;
        const absent = teacherList.filter(t => t.AttendanceType === 'A').length;
        const late = teacherList.filter(t => t.AttendanceType === 'L').length;
        return { total, present, absent, late };
    }, [teacherList]);

    const onPageChange = (e) => {
        setFirst(e.first);
        setRows(e.rows);
    };

    const handleAttendanceMark = (teacherId, value) => {
        setTeacherList((prevList) => {
            const updatedList = prevList.map((item) =>
                item._id === teacherId
                    ? { ...item, AttendanceType: value }
                    : { ...item }
            );
            const allPresent = updatedList.every(
                (item) => item?.AttendanceType === 'P'
            );
            setMarkAllPresent(allPresent);
            return updatedList;
        });
    };

    const handleMarkAllPresent = (checked) => {
        setMarkAllPresent(checked);
        if (checked) {
            setOriginalTeacherList([...teacherList]);
            const updatedList = teacherList.map((teacher) => ({
                ...teacher,
                AttendanceType: 'P',
            }));
            setTeacherList(updatedList);
        } else {
            setTeacherList(originalTeacherList);
        }
    };

    const saveAttendance = async () => {
        try {
            const requestData = teacherList.map(row => ({
                TeacherId: row._id,
                AttendanceType: row?.AttendanceType || '',
                AttendanceDate: moment(selectedDate).format("YYYY-MM-DD"),
                FinancialYear: financialYear,
                SchoolId: schoolId,
                CreatedBy: securityUserID,
            }));
            Loader.show();
            const response = await api.post(APIConstant.path.AddStaffAttendanceDetail, requestData);
            if (response.success) {
                Toast.success({ message: response.message });
                await GetTeacherList(schoolId, financialYear, selectedDate);
                Loader.hide();
            } else {
                Toast.error({ message: 'Failed to save attendance' });
                Loader.hide();
            }
        } catch (error) {
            Loader.hide();
            catchResponse(error);
        }
    };

    const handleDateChange = (date) => {
        setSelectedDate(date);
        setMarkAllPresent(false);
        GetTeacherList(schoolId, financialYear, date);
    };


    const srNoTemplate = (data, props) => {
        return <span className="sr-no-cell">{props.rowIndex + 1}</span>;
    };

    const teacherInfoTemplate = (rowData) => {
        const imageUrl = rowData?.ProfilePic
            ? `${apiBase}/UserProfilePic/${rowData?.ProfilePic}`
            : userImage;

        return (
            <div className="staff-info-cell">
                <img
                    src={imageUrl}
                    alt={rowData.TeacherName}
                    className="staff-avatar"
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = userImage;
                    }}
                />
                <div className="staff-detail">
                    <span className="staff-name">{rowData.TeacherName}</span>
                </div>
            </div>
        );
    };

    const attendanceTemplate = (rowData) => {
        return (
            <div className="attendance-chip-group">
                {attendanceOptions.map((option) => (
                    <div
                        key={option.value}
                        className={`attendance-chip ${option.value} ${rowData?.AttendanceType === option.value ? 'active' : ''}`}
                        onClick={() => handleAttendanceMark(rowData._id, option.value)}
                        title={option.value === 'P' ? 'Present' : option.value === 'A' ? 'Absent' : 'Late'}
                    >
                        {option.label}
                    </div>
                ))}
            </div>
        );
    };

    const handleDownloadSampleFile = async () => {
        try {
            Loader.show();
            const loginData = CommonConfig.loginData();
            const scopeUserId = CommonConfig.isOrgOwner(loginData?.UserRole) ? loginData?._id : loginData?.CreatedBy;
            const res = await api.post(APIConstant.path.GenerateStaffAttendanceSample, {
                accountType: loginData?.AccountType,
                userType: loginData?.UserType,
                userId: scopeUserId,
            });
            if (res.success && res?.sampleFileUrl) {
                const downloadUrl = `${apiBase}${res.sampleFileUrl}`;
                const link = document.createElement("a");
                link.href = downloadUrl;
                link.setAttribute("download", downloadUrl.split('/').pop());
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                Loader.hide();
            } else {
                Loader.hide();
                Toast.error({ message: res?.message || 'Failed to download sample file' });
            }
        } catch (err) {
            Loader.hide();
            Toast.error({ message: err?.message || 'Failed to download sample file' });
            catchResponse(err);
        }
    };

    const handleUploadVisible = () => {
        setIsUploadVisible(false);
    };

    const headerTemplate = (options) => {
        const { className, chooseButton, uploadButton, cancelButton } = options;
        return (
            <div className={className} style={{ backgroundColor: 'transparent', display: 'flex', alignItems: 'center' }}>
                {chooseButton}
                {uploadButton}
                {cancelButton}
            </div>
        );
    };

    const itemTemplate = (file, props) => {
        return (
            <div className="">
                <div className="p-d-flex p-ai-baseline p-jc-between">
                    <div className="flex flex-column text-left ml-3">
                        <p> {file.name} </p>
                        <small>{new Date().toLocaleDateString()}</small>
                    </div>
                </div>
                <Tag value={props.formatSize} severity="warning" className="e-uplod-box" />
                <div className='p-d-flex p-ai-center p-jc-center'>
                    <Button type="button" icon="pi pi-times" className="p-d-flex p-ai-center p-jc-center p-button-outlined p-button-rounded p-button-danger ml-auto p-m-0" onClick={() => onTemplateRemove(file, props.onRemove)} />
                </div>
            </div>
        );
    };

    const emptyTemplate = () => {
        return (
            <div className="flex align-items-center flex-column">
                <i className="pi pi-image mt-3 p-5" style={{ fontSize: '5em', borderRadius: '50%', backgroundColor: 'var(--surface-b)', color: 'var(--surface-d)' }}></i>
                <span style={{ fontSize: '1.2em', color: 'var(--text-color-secondary)' }} className="my-5">
                    Drag and Drop Excel Here
                </span>
            </div>
        );
    };

    const onTemplateUpload = async (e) => {
        const uploadedFile = e.files[0];
        if (!uploadedFile) return;

        try {
            Loader.show();
            const loginData = CommonConfig.loginData();
            const scopeUserId = CommonConfig.isOrgOwner(loginData?.UserRole) ? loginData?._id : loginData?.CreatedBy;
            const formData = new FormData();
            formData.append("file", uploadedFile);
            formData.append("filename", uploadedFile?.name);
            formData.append("accountType", loginData?.AccountType);
            formData.append("userType", loginData?.UserType);
            formData.append("userId", scopeUserId);
            formData.append("takenBy", loginData?._id);
            formData.append("financialYearId", financialYear);
            await api.post(APIConstant.path.ImportStaffAttendance, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
                .then(async (response) => {
                    let res = response;
                    if (res.success) {
                        Loader.hide();
                        if (res?.skippedFileUrl) {
                            var downloadUrl = `${apiBase}${res?.skippedFileUrl}`;
                            const link = document.createElement("a");
                            link.href = downloadUrl;
                            link.setAttribute("download", downloadUrl.split('/').pop());
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        }
                        handleUploadVisible();
                        Toast.success({ message: res?.message });
                        GetTeacherList(schoolId, financialYear, selectedDate);
                    } else {
                        Loader.hide();
                        if (res?.skippedFileUrl) {
                            var downloadcatchUrl = `${apiBase}${res?.skippedFileUrl}`;
                            const link = document.createElement("a");
                            link.href = downloadcatchUrl;
                            link.setAttribute("download", downloadcatchUrl.split('/').pop());
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        }
                        Toast.error({ message: res.message });
                    }
                })
                .catch((err) => {
                    Toast.error({ message: err?.message });
                    Loader.hide();
                });
        } catch (error) {
            Loader.hide();
            const errorMessage = error.response?.data?.message || "File upload failed.";
            Toast.error({ message: errorMessage });
            catchResponse(error);
        }
    };

    const onTemplateRemove = (file, callback) => {
        callback();
    };

    const chooseOptions = { icon: 'pi pi-fw pi-images', iconOnly: true, className: 'p-d-flex p-ai-center p-jc-center p-button-rounded p-button-outlined p-m-0' };
    const uploadOptions = { icon: 'pi pi-fw pi-cloud-upload', iconOnly: true, className: 'p-d-flex p-ai-center p-jc-center p-button-success p-button-rounded p-button-outlined p-m-0' };
    const cancelOptions = { icon: 'pi pi-fw pi-times', iconOnly: true, className: 'p-d-flex p-ai-center p-jc-center p-button-danger p-button-rounded p-button-outlined p-m-0' };

    return (
        <div className='wayment-owner-dashboard et-master-page'>
            <div className="heading-area p-d-flex p-ai-center p-jc-between">
                <div className="p-d-flex p-ai-baseline">
                    <i className="pi pi-users"></i>
                    <h2>Staff Attendance</h2>
                </div>
                <div className="p-grid p-align-center">
                    {pageAccess?.AddAccess &&
                        <Button icon="pi pi-download" onClick={handleDownloadSampleFile} className="e-h-btn p-m-1" />}
                    {pageAccess?.AddAccess &&
                        <Button icon="pi pi-upload" className="e-h-btn p-m-1" onClick={() => setIsUploadVisible(true)} />}
                </div>
            </div>
            <div className="staff-summary-cards p-py-3">
                <div className="summary-card total">
                    <div className="summary-icon">
                        <i className="pi pi-users"></i>
                    </div>
                    <div className="summary-info">
                        <h4>Total Teachers</h4>
                        <h2>{summaryData.total}</h2>
                    </div>
                </div>
                <div className="summary-card present">
                    <div className="summary-icon">
                        <i className="pi pi-check-circle"></i>
                    </div>
                    <div className="summary-info">
                        <h4>Present</h4>
                        <h2>{summaryData.present}</h2>
                    </div>
                </div>
                <div className="summary-card absent">
                    <div className="summary-icon">
                        <i className="pi pi-times-circle"></i>
                    </div>
                    <div className="summary-info">
                        <h4>Absent</h4>
                        <h2>{summaryData.absent}</h2>
                    </div>
                </div>
                <div className="summary-card late">
                    <div className="summary-icon">
                        <i className="pi pi-clock"></i>
                    </div>
                    <div className="summary-info">
                        <h4>Late</h4>
                        <h2>{summaryData.late}</h2>
                    </div>
                </div>
            </div>
            <div className="staff-attendance-header">
                <div className="header-actions">
                    <label>Attendance Date:</label>
                    <div className='searchbar-area'>
                        <Calendar
                            className='p-w-100 p-mt-0'
                            id="staffAttendanceDate"
                            value={selectedDate}
                            minDate={academicStartDate}
                            maxDate={academicEndDate}
                            hourFormat="12"
                            dateFormat="dd/mm/yy"
                            onChange={(e) => handleDateChange(e.value)}
                        />
                    </div>
                </div>
                <div className="header-actions">
                    <div className="mark-all-badge" onClick={() => handleMarkAllPresent(!markAllPresent)}>
                        <Checkbox
                            inputId="mark-all-teacher-present"
                            checked={markAllPresent}
                            onChange={(e) => handleMarkAllPresent(e.checked)}
                        />
                        <label htmlFor="mark-all-teacher-present">Mark All Present</label>
                    </div>
                </div>
            </div>

            <DataTable
                value={teacherList}
                paginator
                rows={rows}
                first={first}
                filterDisplay="menu"
                responsiveLayout="scroll"
                paginatorTemplate="RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink JumpToPageInput"
                rowsPerPageOptions={[10, 20, 50]}
                currentPageReportTemplate="Showing {first}-{last} of {totalRecords} teachers"
                onPage={onPageChange}
                className='custom-table staff-table-wide'
                scrollHeight='calc(100vh - 330px)'
                size='small'
                emptyMessage="No teachers found."
            >
                <Column
                    header="Sr No."
                    body={srNoTemplate}
                    style={{ width: '60px', textAlign: 'center' }}
                />
                <Column
                    header="Teacher Name"
                    body={teacherInfoTemplate}
                />
                <Column
                    header="Phone No."
                    field="Phone"
                />
                <Column
                    header="Email"
                    field="Email"
                />
                <Column
                    header="Attendance"
                    body={attendanceTemplate}
                    style={{ width: '180px' }}
                />
            </DataTable>

            <div className="staff-attendance-footer">
                <Button
                    label="Cancel"
                    className="btn-text-action-secondary"
                    onClick={() => {
                        GetTeacherList(schoolId, financialYear, selectedDate);
                        setMarkAllPresent(false);
                    }}
                />
                <Button
                    label="Save"
                    className="btn-text-action-primary"
                    onClick={saveAttendance}
                />
            </div>

            <Dialog
                visible={isUploadVisible}
                onHide={handleUploadVisible}
                header="Upload Excel"
                draggable={false}
                dismissableMask
                position="center"
                style={{ width: '500px', minHeight: '480px', maxHeight: '480px', }}
            >
                <div>
                    <div className='p-d-fle p-ai-center p-jc-between p-w-100'>
                        <div>
                            <Tooltip className='p-m-0' target=".custom-choose-btn" content="Choose" position="bottom" />
                        </div>
                        <div>
                            <Tooltip className='p-m-0' target=".custom-upload-btn" content="Upload" position="bottom" />
                        </div>
                        <div>
                            <Tooltip className='p-m-0' target=".custom-cancel-btn" content="Clear" position="bottom" />
                        </div>
                        <FileUpload ref={fileUploadRef} name="file" accept=".xlsx" multiple={false} customUpload={true} uploadHandler={onTemplateUpload} headerTemplate={headerTemplate} itemTemplate={itemTemplate} emptyTemplate={emptyTemplate} chooseOptions={chooseOptions} uploadOptions={uploadOptions} cancelOptions={cancelOptions} />
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default StaffAttendance;
